import { observer } from "mobx-react-lite";
import { ZoomControl } from "react-leaflet";
import { Div, Section, Subtitle } from "trunx";
import FindTable from "./FindTable";
import CameraInfoPanel from "./info/CameraInfo";
import CbrfcInfo from "./info/CbrfcInfo";
import FeatureInfo from "./info/FeatureInfo";
import LightningInfo from "./info/LightningInfo";
import SensorInfo from "./info/SensorInfo";
import { IPanel } from "./models/Menus";
import { useRootStore } from "./models/Root";
import { ISelectedSensor } from "./models/SelectedSensor";
import NavMenu from "./NavMenu";
import BookmarksPanel from "./panels/BookmarksPanel";
import LayersPanel from "./panels/LayersPanel";
import ForecastPanel from "./panels/ForecastPanel";
import MeasurePanel from "./panels/MeasurePanel";
import RadarPanel from "./panels/RadarPanel";
import SensorsPanel from "./panels/SensorsPanel";
import Chart from "./panes/Chart";
import TabularDataPane from "./panes/TabularData";
import {
  LeafletPanelBody,
  LeafletSensorPanelHeading
} from "./shared/LeafletPanel";
import LeafletSidebar from "./shared/LeafletSidebar";

const LeftSidebar = observer(() => {
  const getPanel = (panel: IPanel) => {
    switch (panel) {
      case "layers":
        return LayersPanel;
      case "bookmarks":
        return BookmarksPanel;
      case "measure":
        return MeasurePanel;
      case "sensors":
        return SensorsPanel;
      case "radar":
        return RadarPanel;
      case "forecast":
        return ForecastPanel;
      case "find":
        return FindTable;
    }
  };

  const {
    menus: { nav, panel },
  } = useRootStore();

  if (panel) {
    const Panel = getPanel(panel);
    return (
      <LeafletSidebar side="left" size="normal">
        <div className="leaflet-control">
          <Panel />
        </div>
      </LeafletSidebar>
    );
  } else if (nav) {
    return (
      <LeafletSidebar side="left" size="narrow">
        <div className="leaflet-control narrow">
          <NavMenu></NavMenu>
        </div>
      </LeafletSidebar>
    );
  } else {
    return <> </>;
  }
});

const RightSidebarSensorInfo = observer(
  ({ selected }: { selected: ISelectedSensor }) => {
    switch (selected.pane) {
      case "summary":
        return <SensorInfo selected={selected} />;
      case "tabular":
        return <TabularDataPane sensor={selected.sensor} />;
      case "chart":
        return (
          <Div bulma="panel">
            <LeafletSensorPanelHeading sensor={selected.sensor} action="back" />

            <LeafletPanelBody>
              <Section>
                <Subtitle bulma={["has-text-centered", "is-6"]}>
                  {selected.sensor.site}
                </Subtitle>
              </Section>

              <Section>
                <div style={{ height: "400px", width: "100%" }}>
                  <Chart selected={selected} />
                </div>
              </Section>
            </LeafletPanelBody>
          </Div>
        );
    }
  }
);

const RightSidebar = observer(() => {
  const {
    menus: { closePanel, closeNav },
    sensors: { selected: selectedSensor },
    layers: { selectedCamera, selectedFeature, lightning, cbrfc },
  } = useRootStore();

  if (selectedSensor) {
    const size = selectedSensor.pane === "summary" ? "normal" : "full";

    if (size === "full") {
      closePanel();
      closeNav();
    }

    return (
      <LeafletSidebar side="right" size={size}>
        <RightSidebarSensorInfo selected={selectedSensor} />
      </LeafletSidebar>
    );
  } else if (selectedFeature) {
    return (
      <LeafletSidebar side="right" size="normal">
        <FeatureInfo feature={selectedFeature} />
      </LeafletSidebar>
    );
  } else if (
    lightning &&
    (lightning.showLegend || lightning.selected !== undefined)
  ) {
    return (
      <LeafletSidebar side="right" size="normal">
        <LightningInfo lightning={lightning} legend={true} />
      </LeafletSidebar>
    );
  } else if (cbrfc && cbrfc.selected !== undefined) {
    return (
      <LeafletSidebar side="right" size="full">
        <CbrfcInfo cbrfc={cbrfc} />
      </LeafletSidebar>
    );
  } else if (selectedCamera !== undefined) {
    return (
      <LeafletSidebar side="right" size="full">
        <CameraInfoPanel camera={selectedCamera} />
      </LeafletSidebar>
    );
  } else {
    return <></>;
  }
});

const Desktop = () => {
  return (
    <>
      <ZoomControl position="bottomright" />
      <LeftSidebar />
      <RightSidebar />
    </>
  );
};

export default observer(Desktop);
